import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import { isToday, isTomorrow } from 'date-fns';
import useAppointments from 'state/appointments';
import useUser from 'state/user';
import { AppointmentStatusValues, ClientTherapistStatus } from 'resources/constants/config';
import { IMAGES_URL } from 'resources/constants/urls';
import {
    BRAND_GREEN_PRIMARY,
    DARKER_TEXT,
    DARK_TEXT,
    SUCCESS_GREEN,
    WARNING_ORANGE,
    ERROR_RED,
} from 'resources/constants/colors';
import { nom035API } from 'resources/api';
import MoodCheckIn from './components/MoodCheckIn';
import WellnessInsight from './components/WellnessInsight';
import QuickActions from './components/QuickActions';
import FirstAidKit from './components/FirstAidKit';
import JournalEntry from './components/JournalEntry';
import TherapistDetail from './components/TherapistDetail';
import NoProfileSVG from 'resources/img/no-pic-therapist.png';
import { getDisplayDate } from 'utils/date';
import { getDisplayTime } from 'utils/time';
import { getStatusColor } from 'utils';
import { getStatusText } from 'utils/text';

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
`;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const PageStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-bottom: 100px;
`;

// ---------------------------------------------------------------------------
// Section label
// ---------------------------------------------------------------------------

const SectionLabel = styled.p`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #aaa;
    margin: 0 0 8px;
    padding: 0 2px;
`;

// ---------------------------------------------------------------------------
// Next appointment card (compact home version)
// ---------------------------------------------------------------------------

const ApptCard = styled(Link)`
    background: #fff;
    border-radius: 20px;
    padding: 16px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
    display: flex;
    gap: 14px;
    align-items: center;
    text-decoration: none;
    animation: ${fadeUp} 0.4s 0.05s ease both;
    -webkit-tap-highlight-color: transparent;

    &:active {
        opacity: 0.85;
    }
`;

const ApptThumb = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 14px;
    overflow: hidden;
    flex-shrink: 0;
    background: #f0f0f0;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ApptInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const ApptName = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ApptTime = styled.p`
    font-size: 12px;
    color: ${DARK_TEXT};
    margin: 0 0 2px;
`;

const ApptStatus = styled.p`
    font-size: 12px;
    font-weight: 600;
    color: ${({ color }) => color || SUCCESS_GREEN};
    margin: 0;
`;

const ApptBadge = styled.div`
    background: linear-gradient(135deg, ${BRAND_GREEN_PRIMARY}, #d4e600);
    border-radius: 10px;
    padding: 6px 10px;
    flex-shrink: 0;

    span {
        font-size: 20px;
        line-height: 1;
    }
`;

// ---------------------------------------------------------------------------
// Empty appointment state
// ---------------------------------------------------------------------------

const EmptyApptCard = styled.div`
    background: #fff;
    border-radius: 20px;
    padding: 24px 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
    text-align: center;
    animation: ${fadeUp} 0.4s 0.05s ease both;
`;

const EmptyApptText = styled.p`
    font-size: 14px;
    color: ${DARK_TEXT};
    margin: 0 0 14px;
    line-height: 1.5;
`;

const EmptyApptButton = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${BRAND_GREEN_PRIMARY};
    color: #fff;
    border-radius: 24px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 700;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.85; }
`;

const CalendarButton = styled.button`
    background: none;
    border: 1.5px solid ${BRAND_GREEN_PRIMARY}60;
    border-radius: 12px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    color: ${BRAND_GREEN_PRIMARY};
    cursor: pointer;
    margin-top: 6px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    -webkit-tap-highlight-color: transparent;

    &:active { opacity: 0.7; }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getRelativeDate = (dateStr) => {
    const d = new Date(dateStr);
    const time = getDisplayTime(dateStr);
    if (isToday(d)) return `Hoy a las ${time}`;
    if (isTomorrow(d)) return `Mañana a las ${time}`;
    return `${getDisplayDate(dateStr, 'EEEE, d MMMM')} · ${time}`;
};

const generateICS = (appointment) => {
    const start = new Date(appointment.date);
    const end = new Date(start.getTime() + 60 * 60 * 1000); // 1h default
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const name = `${appointment.title ?? ''} ${appointment.name} ${appointment.lastName}`.trim();
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Terappy//Cita//ES',
        'BEGIN:VEVENT',
        `DTSTART:${fmt(start)}`,
        `DTEND:${fmt(end)}`,
        `SUMMARY:Sesión con ${name}`,
        'DESCRIPTION:Sesión de terapia en Terappy',
        'END:VEVENT',
        'END:VCALENDAR',
    ];
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sesion-terappy.ics';
    a.click();
    URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Discover section (no therapist assigned)
// ---------------------------------------------------------------------------

const DiscoverCard = styled.div`
    background: linear-gradient(135deg, ${BRAND_GREEN_PRIMARY}18 0%, #d4e60018 100%);
    border: 1.5px dashed ${BRAND_GREEN_PRIMARY}60;
    border-radius: 20px;
    padding: 20px;
    animation: ${fadeUp} 0.4s 0.15s ease both;
`;

const DiscoverTitle = styled.p`
    font-size: 16px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 4px;
`;

const DiscoverBody = styled.p`
    font-size: 13px;
    color: ${DARK_TEXT};
    margin: 0 0 14px;
    line-height: 1.55;
`;

const DiscoverButton = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${BRAND_GREEN_PRIMARY};
    color: #fff;
    border-radius: 24px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 700;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;

    &:active {
        opacity: 0.85;
    }
`;

// ---------------------------------------------------------------------------
// Trial limit reached card
// ---------------------------------------------------------------------------

const TrialLimitCard = styled.div`
    background: linear-gradient(135deg, #fff3e018 0%, #ffe0b218 100%);
    border: 1.5px solid #f59e0b60;
    border-radius: 20px;
    padding: 20px;
    animation: ${fadeUp} 0.4s 0.15s ease both;
`;

const TrialLimitTitle = styled.p`
    font-size: 16px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 4px;
`;

const TrialLimitBody = styled.p`
    font-size: 13px;
    color: ${DARK_TEXT};
    margin: 0 0 0;
    line-height: 1.55;
`;

// ---------------------------------------------------------------------------
// NOM-035 card
// ---------------------------------------------------------------------------

const Nom035Card = styled.div`
    background: #fff;
    border-radius: 20px;
    padding: 16px 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.07);
    animation: ${fadeUp} 0.4s 0.2s ease both;
    display: flex;
    align-items: center;
    gap: 14px;
`;

const Nom035Info = styled.div`
    flex: 1;
    min-width: 0;
`;

const Nom035Title = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 2px;
`;

const Nom035Sub = styled.p`
    font-size: 12px;
    color: ${DARK_TEXT};
    margin: 0;
`;

const NOM035_RISK_COLORS = {
    nulo: SUCCESS_GREEN, none: SUCCESS_GREEN,
    bajo: '#8bc34a',     low: '#8bc34a',
    medio: WARNING_ORANGE, medium: WARNING_ORANGE,
    alto: ERROR_RED,     high: ERROR_RED,
    muyAlto: '#b71c1c', critical: '#b71c1c',
};

const NOM035_RISK_LABELS = {
    nulo: 'Sin riesgo',    none: 'Sin riesgo',
    bajo: 'Riesgo bajo',   low: 'Riesgo bajo',
    medio: 'Riesgo medio', medium: 'Riesgo medio',
    alto: 'Riesgo alto',   high: 'Riesgo alto',
    muyAlto: 'Riesgo muy alto', critical: 'Riesgo muy alto',
};

const Nom035Widget = ({ isEmployee }) => {
    const history = useHistory();
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        nom035API.getMyAssessments()
            .then((data) => setAssessments(Array.isArray(data?.data) ? data.data : []))
            .catch(() => setAssessments([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return null;

    const latest = assessments.length > 0
        ? assessments.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
        : null;

    const pendingSubtext = isEmployee
        ? 'Pendiente — requerida por tu empresa'
        : 'Conoce tu nivel de riesgo psicosocial laboral';

    if (!latest) {
        return (
            <Nom035Card>
                <span style={{ fontSize: 28 }}>📋</span>
                <Nom035Info>
                    <Nom035Title>Evaluación NOM-035</Nom035Title>
                    <Nom035Sub>{pendingSubtext}</Nom035Sub>
                </Nom035Info>
                <button
                    onClick={() => history.push('/nom035')}
                    style={{
                        background: BRAND_GREEN_PRIMARY,
                        color: '#fff',
                        border: 'none',
                        borderRadius: 14,
                        padding: '8px 14px',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: 'pointer',
                        flexShrink: 0,
                        WebkitTapHighlightColor: 'transparent',
                    }}
                >
                    Iniciar →
                </button>
            </Nom035Card>
        );
    }

    const riskKey = (latest.riskLevel || '').toLowerCase();
    const riskColor = NOM035_RISK_COLORS[riskKey] || DARK_TEXT;
    const riskLabel = NOM035_RISK_LABELS[riskKey] || latest.riskLevel;
    const date = latest.createdAt
        ? new Date(latest.createdAt).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
        : '';

    return (
        <Nom035Card>
            <span style={{ fontSize: 28 }}>📋</span>
            <Nom035Info>
                <Nom035Title>Evaluación NOM-035</Nom035Title>
                <Nom035Sub>Completada · {date}</Nom035Sub>
            </Nom035Info>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                <span
                    style={{
                        display: 'inline-block',
                        background: riskColor + '20',
                        color: riskColor,
                        borderRadius: 10,
                        padding: '4px 10px',
                        fontSize: 11,
                        fontWeight: 700,
                    }}
                >
                    {riskLabel}
                </span>
                <button
                    onClick={() => history.push('/nom035')}
                    style={{
                        background: 'transparent',
                        color: BRAND_GREEN_PRIMARY,
                        border: `1px solid ${BRAND_GREEN_PRIMARY}`,
                        borderRadius: 10,
                        padding: '3px 10px',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: 'pointer',
                        WebkitTapHighlightColor: 'transparent',
                    }}
                >
                    Re-evaluar
                </button>
            </div>
        </Nom035Card>
    );
};

// ---------------------------------------------------------------------------
// Client home component
// ---------------------------------------------------------------------------

const Client = () => {
    const [appointments, appointmentsDispatcher] = useAppointments();
    const [user] = useUser();

    useEffect(() => {
        appointmentsDispatcher.fetchUpcomingStart();
    }, []);

    const nextAppointment = useMemo(() => {
        return appointments.upcomingList
            .filter(({ status }) =>
                status !== AppointmentStatusValues.REJECTED &&
                status !== AppointmentStatusValues.CANCELLED
            )
            .sort((a, b) => new Date(a.date) - new Date(b.date))[0] ?? null;
    }, [appointments]);

    const therapist = user.current?.extraData?.therapist ?? null;
    const isPending = therapist?.status === ClientTherapistStatus.PENDING;
    const businessId = user.current?.extraData?.businessId ?? null;
    const isEmployee = Boolean(businessId);
    const dismissedTrials = user.current?.extraData?.dismissedTrials ?? 0;
    const trialLimitReached = !therapist && dismissedTrials >= 3;

    const handleAddToCalendar = useCallback(() => {
        if (nextAppointment) generateICS(nextAppointment);
    }, [nextAppointment]);

    return (
        <PageStack>
            {/* 1. Emotional check-in */}
            <MoodCheckIn />

            {/* 1b. Espacio Seguro — journaling */}
            <JournalEntry />

            {/* 2. Next appointment (or empty state) */}
            {nextAppointment ? (
                <div>
                    <SectionLabel>Próxima sesión</SectionLabel>
                    <ApptCard to={`/appointment/${nextAppointment.roomId}`}>
                        <ApptThumb>
                            <img
                                src={
                                    nextAppointment.profileImg
                                        ? `${IMAGES_URL}${nextAppointment.profileImg}`
                                        : NoProfileSVG
                                }
                                alt="terapeuta"
                            />
                        </ApptThumb>
                        <ApptInfo>
                            <ApptName>
                                {`${nextAppointment.title ?? ''} ${nextAppointment.name} ${nextAppointment.lastName}`.trim()}
                            </ApptName>
                            <ApptTime>
                                {getRelativeDate(nextAppointment.date)}
                            </ApptTime>
                            <ApptStatus color={getStatusColor(nextAppointment)}>
                                {getStatusText(nextAppointment)}
                            </ApptStatus>
                        </ApptInfo>
                        <ApptBadge>
                            <span>📹</span>
                        </ApptBadge>
                    </ApptCard>
                    <div style={{ textAlign: 'center' }}>
                        <CalendarButton onClick={handleAddToCalendar}>
                            📅 Añadir al calendario
                        </CalendarButton>
                    </div>
                </div>
            ) : (
                <div>
                    <SectionLabel>Próxima sesión</SectionLabel>
                    <EmptyApptCard>
                        <EmptyApptText>
                            No tienes sesiones programadas por ahora.
                            <br />
                            ¿Quieres agendar una?
                        </EmptyApptText>
                        {therapist ? (
                            <EmptyApptButton
                                to={{ pathname: `/terapeutas/${therapist.id}`, state: { from: '/home/resumen' } }}
                            >
                                Agendar nueva cita →
                            </EmptyApptButton>
                        ) : (
                            <EmptyApptButton to="/terapeutas">
                                Buscar terapeuta →
                            </EmptyApptButton>
                        )}
                    </EmptyApptCard>
                </div>
            )}

            {/* 3. Assigned therapist OR discover section */}
            {therapist ? (
                <div>
                    <SectionLabel>Tu terapeuta</SectionLabel>
                    <TherapistDetail therapist={therapist} isPending={isPending} />
                </div>
            ) : trialLimitReached ? (
                <div>
                    <SectionLabel>Tu espacio terapéutico</SectionLabel>
                    <TrialLimitCard>
                        <TrialLimitTitle>🔒 Sesiones exploratorias agotadas</TrialLimitTitle>
                        <TrialLimitBody>
                            Has utilizado todas tus sesiones exploratorias gratuitas.
                            Comunícate con soporte para conocer tus opciones de continuidad.
                        </TrialLimitBody>
                    </TrialLimitCard>
                </div>
            ) : (
                <div>
                    <SectionLabel>Tu espacio terapéutico</SectionLabel>
                    <DiscoverCard>
                        <DiscoverTitle>🌱 Comienza tu camino</DiscoverTitle>
                        <DiscoverBody>
                            Aún no tienes un terapeuta asignado. Explora nuestros profesionales
                            verificados y agenda tu primera sesión exploratoria — sin costo.
                        </DiscoverBody>
                        <DiscoverButton to="/terapeutas">
                            Explorar terapeutas →
                        </DiscoverButton>
                    </DiscoverCard>
                </div>
            )}

            {/* 4. Botiquín de primeros auxilios */}
            <div>
                <SectionLabel>Botiquín emocional</SectionLabel>
                <FirstAidKit />
            </div>

            {/* 5. Quick access shortcuts */}
            <QuickActions hasTherapist={!!therapist} />

            {/* 6. NOM-035 card — solo empleados de empresa */}
            {isEmployee && (
                <div>
                    <SectionLabel>Bienestar laboral</SectionLabel>
                    <Nom035Widget isEmployee={isEmployee} />
                </div>
            )}

            {/* 7. Daily wellness insight */}
            <div>
                <SectionLabel>Para ti hoy</SectionLabel>
                <WellnessInsight />
            </div>
        </PageStack>
    );
};

export default Client;
